// FooterPremiumDark — 5-column footer with tagline, links, copyright.
import React from "react";
import { useTranslation } from "react-i18next";
import { Twitter, Linkedin, Youtube } from "lucide-react";

const COLUMNS = [
    { key: "product", links: ["features", "pricing", "roadmap", "changelog"] },
    { key: "company", links: ["about", "blog", "careers", "contact"] },
    { key: "legal", links: ["privacy", "terms", "cookies", "gdpr", "security", "compliance", "sitemap"] },
    { key: "community", links: ["discord", "discover", "academy", "affiliate"] },
];

// Route mapping — most are static # placeholders, key ones link to real routes.
const LINK_ROUTES = {
    features: "#features",
    pricing: "#pricing",
    discover: "/community/discover",
    blog: "/blog",
    academy: "/academy",
    affiliate: "/affiliate",
    privacy: "/legal/privacy",
    terms: "/legal/terms",
    cookies: "/legal/cookies",
    sitemap: "/sitemap.xml",
};

export default function FooterPremiumDark() {
    const { t } = useTranslation("landing_v2");

    return (
        <footer
            className="relative border-t border-brand-border bg-bg pt-16 lg:pt-24 pb-8"
            data-testid="landing-v2-footer"
        >
            <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12 mb-12">
                    {/* Brand column */}
                    <div className="col-span-2 lg:col-span-2">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-black font-bold text-sm">
                                G
                            </div>
                            <span className="text-text-main font-semibold text-lg tracking-tight">
                                Global Lead Suite
                            </span>
                        </div>
                        <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-xs">
                            {t("footer.tagline")}
                        </p>
                        <div className="flex items-center gap-3">
                            <a
                                href="#"
                                aria-label="X / Twitter"
                                className="p-2 rounded-lg border border-brand-border text-text-secondary hover:text-brand-primary hover:border-brand-primary/40 transition-colors"
                            >
                                <Twitter className="w-4 h-4" />
                            </a>
                            <a
                                href="#"
                                aria-label="LinkedIn"
                                className="p-2 rounded-lg border border-brand-border text-text-secondary hover:text-brand-primary hover:border-brand-primary/40 transition-colors"
                            >
                                <Linkedin className="w-4 h-4" />
                            </a>
                            <a
                                href="#"
                                aria-label="YouTube"
                                className="p-2 rounded-lg border border-brand-border text-text-secondary hover:text-brand-primary hover:border-brand-primary/40 transition-colors"
                            >
                                <Youtube className="w-4 h-4" />
                            </a>
                        </div>
                    </div>

                    {/* Link columns */}
                    {COLUMNS.map((col) => (
                        <div key={col.key} className="lg:col-span-1">
                            <h4 className="text-text-main text-sm font-semibold tracking-tight mb-4">
                                {t(`footer.columns.${col.key}.title`)}
                            </h4>
                            <ul className="space-y-3">
                                {col.links.map((linkKey) => {
                                    const isExternal = linkKey === "sitemap";
                                    return (
                                        <li key={linkKey}>
                                            <a
                                                href={LINK_ROUTES[linkKey] || "#"}
                                                target={isExternal ? "_blank" : undefined}
                                                rel={isExternal ? "noopener noreferrer" : undefined}
                                                className="text-text-secondary text-sm hover:text-text-main transition-colors"
                                                data-testid={`landing-v2-footer-${col.key}-${linkKey}`}
                                            >
                                                {t(`footer.columns.${col.key}.links.${linkKey}`)}
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Bottom row */}
                <div className="pt-8 border-t border-brand-border flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <p className="text-xs text-text-secondary">
                        {t("footer.copyright")}
                    </p>
                    <p className="text-xs text-text-secondary/60 italic">
                        {t("footer.made_with")}
                    </p>
                </div>
            </div>
        </footer>
    );
}
